import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, signInWithCustomToken, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { client } from '../apollo';
import { GET_FIREBASE_CUSTOM_TOKEN } from '../authService/queries';
import { authStore } from '../../../store/authStore';
import { getFirebaseConfig } from '../../../config/firebase-config';

class FirebaseService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private firestore: Firestore | null = null;
  private authStateUnsubscribe: (() => void) | null = null;

  constructor() {
    this.initializeFirebase();
  }

  private initializeFirebase(): void {
    try {
      // Check if Firebase is already initialized
      const existingApps = getApps();
      if (existingApps.length > 0) {
        this.app = existingApps[0];
      } else {
        this.app = initializeApp(getFirebaseConfig());
      }

      this.auth = getAuth(this.app);
      this.firestore = getFirestore(this.app);

      // Monitor auth state changes
      this.authStateUnsubscribe = onAuthStateChanged(this.auth, (user: User | null) => {
        if (user) {
        } else {
        }
      });
    } catch (error: any) {
      throw new Error(`Firebase initialization failed: ${error?.message || error}`);
    }
  }

  getAuth(): Auth {
    if (!this.auth) {
      throw new Error('Firebase Auth not initialized');
    }
    return this.auth;
  }

  getFirestore(): Firestore {
    if (!this.firestore) {
      throw new Error('Firestore not initialized');
    }
    return this.firestore;
  }

  getCurrentUser(): User | null {
    return this.auth?.currentUser || null;
  }

  isAuthenticated(): boolean {
    return !!this.auth?.currentUser;
  }

  async signInWithCustomToken(customToken: string): Promise<void> {
    if (!this.auth) {
      throw new Error('Firebase Auth not initialized');
    }

    try {
      await signInWithCustomToken(this.auth, customToken);
    } catch (error: any) {
      throw new Error(`Firebase authentication failed: ${error.message}`);
    }
  }

  async ensureAuthenticated(): Promise<void> {
    // Check if already authenticated
    if (this.isAuthenticated()) {
      return;
    }

    // Check if we have auth tokens from the main auth system
    if (!authStore.isAuthenticated || !authStore.user?.id) {
      throw new Error('User not authenticated. Please sign in first.');
    }

    try {
      // Get Firebase custom token from backend
      const { data } = await client.mutate({
        mutation: GET_FIREBASE_CUSTOM_TOKEN,
        context: {
          headers: {
            Authorization: `Bearer ${authStore.tokens.accessToken}`,
          },
        },
      });

      if (!data?.getFirebaseCustomToken) {
        throw new Error('Failed to get Firebase custom token from backend');
      }

      // Sign in with custom token
      await this.signInWithCustomToken(data.getFirebaseCustomToken);
    } catch (error: any) {
      throw error;
    }
  }

  async signOut(): Promise<void> {
    if (this.auth) {
      await this.auth.signOut();
    }
  }

  cleanup(): void {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
      this.authStateUnsubscribe = null;
    }
  }
}

const firebaseService = new FirebaseService();

export default firebaseService;
